import { effect, inject, Injectable, Signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    AuthorizationsObject,
    AuthService,
    OAuthProfile,
    UserProfileService,
} from '@dat/angular-auth';
import {
    IqEventBusBrokerService,
    IqEventBusQueueService,
} from '@dat/iq-event-bus';
import {
    LogOtelExporters,
    LogOtelProcessors,
    LogOtelService,
    LogOtelUser,
    otelLogFactory,
} from '@dat/log-beta';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { combineLatest, filter, Observable, switchMap } from 'rxjs';
import {
    OTEL_ENDPOINT,
    OTEL_HEADERS,
    OBSERVE_SERVICE_NAME,
    OBSERVE_SERVICE_VERSION,
} from '../../models';
import { IqTrackingOtelMapperService } from './iq-tracking-otel-mapper.service';
import {
    OtelEventPayload,
    OtelEventPayloadSchema,
} from './iq-tracking-otel.models';

type IqTrackingOtelState = {
    userLoaded: boolean;
};

@Injectable({
    providedIn: 'root',
})
export class IqTrackingOtelService extends ComponentStore<IqTrackingOtelState> {
    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _auth: AuthService = inject(AuthService);
    private _user: UserProfileService = inject(UserProfileService);
    private readonly authorizations: Signal<AuthorizationsObject> = toSignal(
        this._auth.userAuthorizationsResult$,
    );
    /**
     * Because we need to pull in user info which may not be loaded on load,
     * we will create an internal event queue so that once the user is loaded
     * we will run them.
     */
    private _queue: IqEventBusQueueService = new IqEventBusQueueService();
    private _otel: LogOtelService;
    private _mapper: IqTrackingOtelMapperService = inject(
        IqTrackingOtelMapperService,
    );
    private _serviceName: string = inject<string>(OBSERVE_SERVICE_NAME);
    private _serviceVersion: string = inject<string>(OBSERVE_SERVICE_VERSION);
    private _endPoint: string = inject<string>(OTEL_ENDPOINT);
    private _headers: { Authorization: string } = inject<{
        Authorization: string;
    }>(OTEL_HEADERS);

    //#region Constructor
    constructor() {
        super({
            userLoaded: false,
        });

        //#region Registering the event bus
        this._bus.registerMap({
            /**
             * This is an event to log non critical events. This
             * can be used to keep track of non critical errors.
             */
            'type:tracking:log': {
                callback: (payload: OtelEventPayload) => {
                    if (payload.type !== 'OTEL') return;

                    try {
                        const checkoutPayload: OtelEventPayload =
                            OtelEventPayloadSchema.parse(payload);

                        this._queue.addEvent({
                            name: 'LOG',
                            payload: checkoutPayload,
                        });
                    } catch (error) {}
                },
            },
            /**
             * This is an event to log JS errors. This
             * can be used to keep track of JS errors.
             */
            'type:tracking:error': {
                callback: (payload: OtelEventPayload) => {
                    if (payload.type !== 'OTEL') return;

                    try {
                        const checkoutPayload: OtelEventPayload =
                            OtelEventPayloadSchema.parse(payload);

                        this._queue.addEvent({
                            name: 'ERROR',
                            payload: checkoutPayload,
                        });
                    } catch (error) {}
                },
            },
            /**
             * This is an event for warn. Only use this if the issue your
             * seeing isn't a blocker but we want to be able to trace it.
             */
            'type:tracking:warning': {
                callback: (payload: OtelEventPayload) => {
                    if (payload.type !== 'OTEL') return;

                    try {
                        const checkoutPayload: OtelEventPayload =
                            OtelEventPayloadSchema.parse(payload);

                        this._queue.addEvent({
                            name: 'WARN',
                            payload: checkoutPayload,
                        });
                    } catch (error) {}
                },
            },
        });
        //# endregion

        //#region Otel initialization
        const config = {
            serviceName: this._serviceName,
            serviceVersion: this._serviceVersion,
            exporters: [
                {
                    type: LogOtelExporters.EndPoint,
                    processor: LogOtelProcessors.Batch,
                    settings: {
                        url: this._endPoint,
                        headers: this._headers,
                    },
                },
            ],
        };

        this._otel = otelLogFactory(config);
        //#endregion

        effect(() => {
            /**
             * If the user is not loaded, we will wait until it is loaded
             * so that all otel traces are pushed out.
             */
            if (!this.userLoaded()) return;

            const unhandled = this._queue.unhandledEventNodeMap();

            if (this._queue.isAllHandled()) return;

            //#region JS Error Process

            /**
             * Gets all the JS errors that haven't been handled
             */
            const jsErrors = unhandled.get('JS_ERROR');

            untracked(() => {
                /**
                 * Iterates through the JS errors and pushes them to the otel
                 */
                jsErrors?.forEach((event) => {
                    const props = event.evt.payload as OtelEventPayload;
                    const info: { error: Error } = props.info as {
                        error: Error;
                    };

                    this._otel.jsError(props.message, info.error, info);
                    this._queue.handleEvent(event);
                });
            });
            //#endregion

            //#region Log Process
            const logs = unhandled.get('LOG');

            untracked(() => {
                logs?.forEach((event) => {
                    const props = event.evt.payload as OtelEventPayload;

                    this._otel.log(props.message, props.info);
                    this._queue.handleEvent(event);
                });
            });
            //#endregion

            //#region Error Process

            const errors = unhandled.get('ERROR');

            untracked(() => {
                errors?.forEach((event) => {
                    const props = event.evt.payload as OtelEventPayload;

                    this._otel.error(props.message, props.info);
                    this._queue.handleEvent(event);
                });
            });
            //#endregion

            //#region Warn Process

            const warns = unhandled.get('WARN');

            untracked(() => {
                warns?.forEach((event) => {
                    const props = event.evt.payload as OtelEventPayload;

                    this._otel.warn(props.message, props.info);
                    this._queue.handleEvent(event);
                });
            });

            //#endregion
        });
    }
    //#endregion

    initialize = this.effect(($trigger) => {
        return $trigger.pipe(
            tapResponse({
                next: () => {
                    this.addUser();
                },
                error: (error) => {
                    console.error('Initialize otel failed', error);
                },
            }),
        );
    });

    //#region Effects

    addUser = this.effect(($trigger) => {
        return $trigger.pipe(
            switchMap(() =>
                combineLatest([
                    this._user.userProfile$,
                    this._auth.userAuthorizationsResult$,
                ]),
            ),
            filter(
                ([user, authorizations]: [
                    OAuthProfile,
                    AuthorizationsObject,
                ]) => !isNaN(user.csbId) && !!authorizations,
            ),
            tapResponse({
                next: async ([userProfile, authorizations]: [
                    OAuthProfile,
                    AuthorizationsObject,
                ]) => {
                    if (this.userLoaded()) return;

                    const userInfo: LogOtelUser = this._mapper.userInfo(
                        userProfile,
                        authorizations,
                    );

                    this._otel.setUser(userInfo);
                    this.updateUserInfo(userInfo);
                },
                error: (error) => {
                    console.error('Identify user failed', error);
                },
            }),
        );
    });

    /**
     * Whenever there is a JS Error. This will take the error messages and
     * adds them to the event queue.
     */
    jsError = this.effect(
        (
            error$: Observable<{
                message: string;
                error: Error;
                info: unknown;
            }>,
        ) => {
            return error$.pipe(
                tapResponse({
                    next: ({ message, error, info }) => {
                        const payload: OtelEventPayload = {
                            type: 'OTEL',
                            message,
                            info: {
                                ...(info ?? {}),
                                error,
                            },
                        };
                        this._queue.addEvent({
                            name: 'JS_ERROR',
                            payload,
                        });
                    },
                    error: (error) => {
                        console.error('JS Error failed', error);
                    },
                }),
            );
        },
    );

    //#endregion

    //#region Updaters
    updateUserInfo = this.updater((state, userInfo: Partial<LogOtelUser>) => ({
        ...state,
        userLoaded: true,
        userInfo,
    }));
    //#endregion

    //#region Selectors
    readonly userLoaded = this.selectSignal((state) => state.userLoaded);
    //#endregion
}
